"use client";

import ReorderGroupWrapper from "../ReorderGroupWrapper";
import RichTextInput from "@/components/ui/RichTextInput";
import ProductSection from "./EditProductSection";
import Image from "next/image";
import { v4 as uuidv4 } from "uuid";
import { ProductClientState } from "@/zod/product.schema";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { UserSVG } from "@/components/ui/SVG";

interface HighlightsSectionProps {
  currentLang: "en" | "fa";
  onHighlightImageChange?: (file: File, highlightUuid: string) => Promise<void>;
  onHighlightAssetPick?: (highlightUuid: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  submitAttempted?: boolean;
}

export function HighlightsSection({
  currentLang,
  onHighlightImageChange,
  onHighlightAssetPick,
  isOpen,
  onToggle,
  submitAttempted = false,
}: HighlightsSectionProps) {
  const { setValue, control, formState } = useFormContext<ProductClientState>();

  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const highlights = useWatch({ control, name: "highlights" });
  const invalid = !!formState.errors.highlights;

  const dir = currentLang === "fa" ? "rtl" : "ltr";
  const editorAlign = currentLang === "fa" ? "text-right" : "text-left";

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    highlightUuid: string,
  ) => {
    if (!onHighlightImageChange) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingId(highlightUuid);
    try {
      await onHighlightImageChange(file, highlightUuid);
    } catch (error) {
      console.error("Failed to upload highlight image:", error);
    } finally {
      setUploadingId(null);
      e.target.value = "";
    }
  };

  return (
    <ProductSection
      title="Highlights"
      faTitle="ویژگی های مهم"
      showFa={currentLang === "fa"}
      dir={dir}
      textAlign={currentLang === "fa" ? "justify" : "left"}
      open={isOpen}
      onToggle={onToggle}
      invalid={submitAttempted && invalid}
    >
      <ReorderGroupWrapper<{
        value: {
          image_url: string;
          assetId?: string | null;
          title_en: string;
          title_fa: string;
          info: { text_en: string; text_fa: string }[];
          uuid: string;
        };
      }>
        handlePosition="top"
        initialItems={(highlights || []).map((h) => ({
          value: {
            image_url: h.image_url || "",
            assetId: h.assetId || null,
            // soft-migration: keep whatever is stored
            title_en: h.title_en || "",
            title_fa: h.title_fa || "",
            uuid: h.uuid || "",
            info: (h.info || []).map((info) => ({
              // soft-migration: keep whatever is stored
              text_en: info.text_en || "",
              text_fa: info.text_fa || "",
            })),
          },
        }))}
        confirmDeleteMessage={
          currentLang === "fa"
            ? "با حذف این ویژگی، عنوان و تصویر و متن‌های انگلیسی و فارسی آن هر دو حذف می‌شوند. ادامه می‌دهید؟"
            : "Deleting this highlight will remove its image, title and info in both EN and FA. Continue?"
        }
        newItemTemplate={{
          value: {
            image_url: "",
            assetId: null,
            title_en: "",
            title_fa: "",
            info: [{ text_en: "", text_fa: "" }],
            uuid: uuidv4(),
          },
        }}
        onChange={(vals) =>
          setValue(
            "highlights",
            vals.map((val) => val.value),
          )
        }
        renderItem={({ value: item }, index, update) => (
          <div className="border-slateCustom flex flex-col border-b">
            <div className="border-slateCustom my-6 rounded-md border">
              {uploadingId === item.uuid && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-black/50 text-white">
                  Uploading...
                </div>
              )}

              <label
                htmlFor={`highlightImage-${item.uuid}`}
                className="relative flex h-80 flex-col items-center justify-center overflow-hidden rounded-md text-gray-500"
                onClick={(e) => {
                  if (onHighlightAssetPick) {
                    e.preventDefault();
                    onHighlightAssetPick(item.uuid);
                  }
                }}
              >
                {item.image_url ? (
                  <Image
                    src={item.image_url}
                    alt="Preview"
                    width={500}
                    height={500}
                  />
                ) : (
                  <>
                    <UserSVG className="my-1 w-16 rounded-sm" />
                    <p>Upload photo</p>
                    <span className="text-center text-xs font-light">
                      dims: 372 x 256 <br />
                      size: less than 1MB
                    </span>
                  </>
                )}

                {uploadingId === item.uuid && (
                  <div className="bg-opacity-60 absolute inset-0 z-10 flex items-center justify-center bg-white">
                    <div className="border-slateCustom h-10 w-10 animate-spin rounded-full border-b-4"></div>
                  </div>
                )}
              </label>

              {submitAttempted && (
                <p className="mt-1 px-2 text-xs text-red-500">
                  {(formState.errors as any)?.highlights?.[index]?.image_url
                    ?.message || ""}
                </p>
              )}

              {!onHighlightAssetPick ? (
                <input
                  type="file"
                  className="hidden"
                  id={`highlightImage-${item.uuid}`}
                  name={`highlightImage-${item.uuid}`}
                  onChange={(e) => handleFileChange(e, item.uuid)}
                />
              ) : null}
            </div>

            <RichTextInput
              placeholder={currentLang === "fa" ? "عنوان" : "Title"}
              // ✅ soft migration
              value={item[`title_${currentLang}`] ?? ""}
              // ✅ JSON-only save
              onChange={({ json }) =>
                update({
                  value: {
                    ...item,
                    [`title_${currentLang}`]: JSON.stringify(json),
                  },
                })
              }
              className="w-7/12 rounded-md border border-gray-200 bg-white"
              editorClassName={editorAlign}
              dir={dir as "ltr" | "rtl"}
              minHeightClassName="min-h-[3rem]"
            />

            {submitAttempted && (
              <p className="-mt-2 text-xs text-red-500">
                {(formState.errors as any)?.highlights?.[index]?.title_en
                  ?.message ||
                  (formState.errors as any)?.highlights?.[index]?.title_fa
                    ?.message ||
                  ""}
              </p>
            )}

            <ReorderGroupWrapper<{
              value: { text_en: string; text_fa: string };
            }>
              initialItems={(item.info || []).map((info) => ({ value: info }))}
              newItemTemplate={{ value: { text_en: "", text_fa: "" } }}
              confirmDeleteMessage={
                currentLang === "fa"
                  ? "با حذف این مورد، متن انگلیسی و فارسی آن هر دو حذف می‌شوند. ادامه می‌دهید؟"
                  : "Deleting this point will remove both EN and FA texts. Continue?"
              }
              onChange={(updatedInfoItems) => {
                const newInfoArray = updatedInfoItems.map((i) => i.value);
                update({
                  value: {
                    ...item,
                    info: newInfoArray,
                  },
                });
              }}
              renderItem={({ value: infoItem }, infoIndex, updateInfo) => (
                <div>
                  <RichTextInput
                    placeholder={currentLang === "fa" ? "متن" : "Info"}
                    // ✅ soft migration
                    value={infoItem[`text_${currentLang}`] ?? ""}
                    // ✅ JSON-only save
                    onChange={({ json }) =>
                      updateInfo({
                        value: {
                          ...infoItem,
                          [`text_${currentLang}`]: JSON.stringify(json),
                        },
                      })
                    }
                    className="rounded-md border border-gray-200 bg-white"
                    editorClassName={editorAlign}
                    dir={dir as "ltr" | "rtl"}
                    minHeightClassName="min-h-[3rem]"
                  />

                  {submitAttempted && (
                    <p className="-mt-2 text-xs text-red-500">
                      {(formState.errors as any)?.highlights?.[index]?.info?.[
                        infoIndex
                      ]?.text_en?.message ||
                        (formState.errors as any)?.highlights?.[index]?.info?.[
                          infoIndex
                        ]?.text_fa?.message ||
                        ""}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        )}
      />
    </ProductSection>
  );
}
